import {
  createGenericSelectContentEvent,
  trackGAEvent,
} from '@moonpig/web-core-analytics'
import { getDepartmentTitle } from '@moonpig/web-core-utils'
import { DepartmentsEnum } from '@moonpig/web-explore-types-graphql'
import {
  SearchStoreState,
  TrackingActionTypes,
  TrackingActionsEnum,
  TrackingPayload,
} from '../types'
import { getTrackingLabelForSortOrder } from '../../utils/getTrackingLabel'
import { GALLERY_PAGE_TYPE, SEARCH_PAGE_TYPE } from '../../constants'
import { createSearchGAEvent } from '../../analytics/createSearchGAEvent'
import { createViewGalleryResultsGAEvent } from '../../analytics/createViewGalleryResultsGAEvent'
import { QueryProductsProps } from '../slices/products'
import { trackLoadedProductsEvent } from './events'
import { generateDepartmentHeadersClickGAEvent } from '../../analytics/GAEvents'
import { ClearFiltersProps } from '../slices/filters'
import { getClearFiltersEventLabel } from '../helpers'
import { ApplyRangeFilterProps } from '../slices/filters/types'

export const trackEvent = ({
  action,
  oldValues,
  newValues,
  payload,
}: {
  action: TrackingActionTypes
  payload: TrackingPayload
  oldValues: SearchStoreState
  newValues: SearchStoreState
}) => {
  switch (action) {
    case TrackingActionsEnum.toggleFiltersMenu:
      trackGAEvent(
        createGenericSelectContentEvent({
          eventCategory: `${newValues.pageContext.type} action`,
          eventAction: newValues.filtersMenuOpen
            ? 'filters opened'
            : 'filters closed',
          eventLabel: newValues.pageContext.title,
        }),
      )
      break

    case TrackingActionsEnum.toggleFiltersCategory:
      trackGAEvent(
        createGenericSelectContentEvent({
          eventCategory: `${newValues.pageContext.type} action`,
          eventAction:
            newValues.selectedCategories.length >
            oldValues.selectedCategories.length
              ? 'expand'
              : 'collapse',
          eventLabel:
            newValues.selectedCategories.length >
            oldValues.selectedCategories.length
              ? newValues.selectedCategories[
                  newValues.selectedCategories.length - 1
                ].label
              : oldValues.selectedCategories[
                  oldValues.selectedCategories.length - 1
                ]?.label || /* istanbul ignore next */ '',
        }),
      )
      break

    case TrackingActionsEnum.clearFilters:
      trackGAEvent(
        createGenericSelectContentEvent({
          eventCategory: `${newValues.pageContext.type} action`,
          eventAction: 'clear filters',
          eventLabel: getClearFiltersEventLabel(payload as ClearFiltersProps[]),
        }),
      )

      break

    case TrackingActionsEnum.sortBy:
      trackGAEvent(
        createGenericSelectContentEvent({
          eventCategory: `${newValues.pageContext.type} action`,
          eventAction: 'sort by',
          eventLabel: getTrackingLabelForSortOrder(payload[0] as string),
        }),
      )
      break

    case TrackingActionsEnum.changeDepartment: {
      const searchTerm =
        newValues.searchContext.searchTerm ||
        newValues.searchContext.selectedSuggestion?.label ||
        ''
      const departmentName = payload[0] as DepartmentsEnum
      const departmentTitle = getDepartmentTitle(departmentName)
      const departmentCount =
        newValues.searchContext.departmentCounts.find(
          d => d.department === departmentName,
        )?.count || /* istanbul ignore next */ 0

      trackGAEvent(
        generateDepartmentHeadersClickGAEvent(
          searchTerm,
          departmentTitle,
          departmentCount,
        ),
      )

      break
    }

    case TrackingActionsEnum.queryProducts:
      if (!(payload[0] as QueryProductsProps).isLoadMore) {
        if (newValues.pageContext.type === SEARCH_PAGE_TYPE) {
          const selectedFilters =
            (payload[0] as QueryProductsProps).filters ||
            /* istanbul ignore next */ newValues.selectedFilters
          trackGAEvent(
            createSearchGAEvent({
              correctedSearchTerm:
                newValues.searchContext.correctedSearchTerm || '',
              department: newValues.searchContext.departments,
              filters: selectedFilters.map(f => ({
                key: f.id,
                group: f.parent,
              })),
              filteringEvent: newValues.filteringEvent,
              numberOfResults: newValues.results.totalCount,
              searchTerm: newValues.searchContext.searchTerm,
              sort: newValues.sortValue,
              selectedSuggestion: newValues.searchContext.selectedSuggestion,
              isTrendingSuggestion:
                !!newValues.searchContext.isTrendingSuggestion,
              showNbas: newValues.hasNbaFilters,
              showRudeProducts: newValues.hasRudeProducts,
              redirectedDepartment:
                newValues.searchContext.redirectedDepartment,
              redirectionSource: newValues.searchContext.redirectionSource,
            }),
          )

          trackLoadedProductsEvent({
            results: newValues.results,
            searchContext: newValues.searchContext,
            preAppliedFilters: newValues.preAppliedFilters,
            selectedFilters: newValues.selectedFilters,
            pageContext: newValues.pageContext,
            lastLoadedPage: newValues.lastLoadedPage,
            filteringEvent: newValues.filteringEvent,
            isPrevious: payload[0] as boolean,
          })
        }

        if (newValues.pageContext.type === GALLERY_PAGE_TYPE) {
          const selectedFilters =
            (payload[0] as QueryProductsProps).filters ||
            newValues.selectedFilters
          trackGAEvent(
            createViewGalleryResultsGAEvent({
              department: newValues.searchContext.departments,
              filters: [
                ...newValues.preAppliedFilters,
                ...selectedFilters.map(f => ({
                  key: f.id,
                  group: f.parent,
                })),
              ],
              filteringEvent: newValues.filteringEvent,
              numberOfResults: newValues.results.totalCount,
              pageTitle: newValues.pageContext.title,
              sort: newValues.sortValue,
              showNbas: newValues.hasNbaFilters,
              showRudeProducts: newValues.hasRudeProducts,
            }),
          )

          trackLoadedProductsEvent({
            results: newValues.results,
            searchContext: newValues.searchContext,
            preAppliedFilters: newValues.preAppliedFilters,
            selectedFilters: newValues.selectedFilters,
            pageContext: newValues.pageContext,
            lastLoadedPage: newValues.lastLoadedPage,
            filteringEvent: newValues.filteringEvent,
            isPrevious: payload[0] as boolean,
          })
        }
      }
      break

    case TrackingActionsEnum.loadMoreProducts:
      trackLoadedProductsEvent({
        results: newValues.results,
        searchContext: newValues.searchContext,
        preAppliedFilters: newValues.preAppliedFilters,
        selectedFilters: newValues.selectedFilters,
        pageContext: newValues.pageContext,
        lastLoadedPage: newValues.lastLoadedPage,
        filteringEvent: newValues.filteringEvent,
        isPrevious: payload[0] as boolean,
      })
      break

    case TrackingActionsEnum.applyRangeFilter:
      trackGAEvent(
        createGenericSelectContentEvent({
          eventCategory: `${newValues.pageContext.type} action`,
          eventAction: 'show milestone results',
          eventLabel: (payload[0] as ApplyRangeFilterProps).value,
        }),
      )
      break
  }
}
